import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import VisitingCardDisplay from '../components/VisitingCardDisplay';
import AnalyticsModal from '../components/AnalyticsModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  User, Mail, Calendar, QrCode, Eye, Download,
  LogOut, Sparkles, BarChart3, Crown, Check, X, Gift, Phone, Camera, Loader2, Package, ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserCard } from '../hooks/useUserCard';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Profile = () => {
  const { user, signOut, updateProfilePhoto } = useAuth();
  const { userCard, loading } = useUserCard();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [userCards, setUserCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [premiumEnabled, setPremiumEnabled] = useState(true);
  const [pricingEnabled, setPricingEnabled] = useState(true);
  const [selectedCardViews, setSelectedCardViews] = useState(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [loadingViewDetails, setLoadingViewDetails] = useState(false);
  const [cardAnalytics, setCardAnalytics] = useState({});
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedCardForAnalytics, setSelectedCardForAnalytics] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [editPhoneOpen, setEditPhoneOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [showCardPremiumModal, setShowCardPremiumModal] = useState(false);
  const [selectedCardForPremium, setSelectedCardForPremium] = useState(null);
  const fileInputRef = React.useRef(null);
  
  // Get cards that need premium (expired or no premium)
  const cardsNeedingPremium = userCards.filter(card => {
    const isPremiumActive = card.is_premium_card && 
      card.premium_expires_at && 
      new Date(card.premium_expires_at) > new Date();
    return !isPremiumActive;
  });

  // Detect navigation from card creation or payment success
  useEffect(() => {
    if (location.state?.newCard) {
      // Trigger a refresh when coming from card creation
      setRefreshTrigger(prev => prev + 1);
      
      // Clear the state so it doesn't trigger again
      window.history.replaceState({}, document.title);
    }
    
    // Check for payment success in URL
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('payment') === 'success') {
      toast({
        title: "🎉 Subscription Activated!",
        description: "Welcome to your premium features!",
        duration: 3000
      });
      
      // Scroll to cards section
      setTimeout(() => {
        const cardsSection = document.getElementById('my-cards-section');
        if (cardsSection) {
          cardsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 1000);
      
      // Clean up URL
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location, toast]);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!user || !premiumEnabled) return;
      
      try {
        const { subscriptionAPI } = await import('../services/api');
        const status = await subscriptionAPI.getStatus();
        setSubscriptionStatus(status);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    fetchSubscriptionStatus();
  }, [user, premiumEnabled]);

  // Fetch pricing configuration
  useEffect(() => {
    const fetchPricingConfig = async () => {
      try {
        const { adminAPI } = await import('../services/api');
        const config = await adminAPI.getPricingConfig();
        setPremiumEnabled(config.premiumEnabled !== false);
        setPricingEnabled(config.pricingPageEnabled !== false);
      } catch (error) {
        console.log('Using default pricing config');
        setPremiumEnabled(true);
        setPricingEnabled(true);
      }
    };

    fetchPricingConfig();
  }, []);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const { cardAPI, analyticsAPI } = await import('../services/api');
        
        // Fetch user's cards
        const cards = await cardAPI.getByUserId(user.uid);
        
        // Fetch analytics for each card to sort by views
        const analyticsPromises = cards.map(async (card) => {
          try {
            const cardId = card.id || card._id;
            const [viewsData, savesData] = await Promise.all([
              analyticsAPI.getCardViews(cardId),
              analyticsAPI.getCardSaves(cardId).catch(() => ({ total_saves: 0 }))
            ]);
            return {
              cardId,
              totalViews: viewsData.totalViews || 0,
              totalSaves: savesData.total_saves || card.save_count || 0
            };
          } catch (error) {
            return {
              cardId: card.id || card._id,
              totalViews: 0,
              totalSaves: 0
            };
          }
        });
        
        const analytics = await Promise.all(analyticsPromises);
        const analyticsMap = {};
        analytics.forEach(a => {
          analyticsMap[a.cardId] = { views: a.totalViews, saves: a.totalSaves };
        });
        setCardAnalytics(analyticsMap);
        
        // Sort cards by total views (most viewed first)
        const sortedCards = cards.sort((a, b) => {
          const aViews = analyticsMap[a.id || a._id]?.views || 0;
          const bViews = analyticsMap[b.id || b._id]?.views || 0;
          return bViews - aViews;
        });
        
        setUserCards(sortedCards);
        setLoadingCards(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoadingCards(false);
      }
    };

    fetchData();
  }, [user, refreshTrigger]);

  // Fetch orders
  useEffect(() => {
    if (!user) return;

    const fetchOrders = async () => {
      try {
        setLoadingOrders(true);
        const { ordersAPI } = await import('../services/api');
        const ordersData = await ordersAPI.getMyOrders();
        setOrders(ordersData.slice(0, 5)); // Show only recent 5 orders
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchOrders();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed Out",
        description: "You have been signed out successfully."
      });
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File",
        description: "Please select an image file.",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image under 5MB.",
        variant: "destructive"
      });
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convert to base64 and compress
      const compressedImage = await compressImage(file);
      
      // Update profile via API
      const { updateUserProfile } = await import('../contexts/AuthContext').then(m => ({ updateUserProfile: m.useAuth }));
      
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ photoURL: compressedImage })
      });

      if (!response.ok) {
        throw new Error('Failed to update profile photo');
      }

      toast({
        title: "Photo Updated",
        description: "Your profile photo has been updated."
      });

      // Refresh to update user context with new photo
      window.location.reload();
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload photo. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUploadingPhoto(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Save phone number to profile
  const handleSavePhone = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      toast({
        title: "Invalid Phone",
        description: "Please enter a valid 10-digit phone number.",
        variant: "destructive"
      });
      return;
    }

    setSavingPhone(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ phoneNumber })
      });

      if (!response.ok) {
        throw new Error('Failed to update phone number');
      }

      toast({
        title: "Phone Updated",
        description: "Your phone number has been saved."
      });
      
      setEditPhoneOpen(false);
      // Refresh to update user context
      window.location.reload();
    } catch (error) {
      console.error('Error saving phone:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save phone number. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSavingPhone(false);
    }
  };

  // Helper function to compress image
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 400; // Max dimension
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressed = canvas.toDataURL('image/jpeg', 0.7);
          resolve(compressed);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleViewAnalytics = async (card) => {
    setSelectedCardForAnalytics({
      id: card.id || card._id,
      name: card.name
    });
    setShowAnalyticsModal(true);
  };

  const handleShare = async (card) => {
    const cardUrl = `${window.location.origin}/bio/${card.username}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${card.name}'s Digital Business Card`,
          text: `Check out ${card.name}'s digital business card on Piper Card`,
          url: cardUrl
        });
        toast({
          title: "Shared!",
          description: "Card link shared successfully."
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to clipboard
          navigator.clipboard.writeText(cardUrl);
          toast({
            title: "Link Copied!",
            description: "Card link copied to clipboard."
          });
        }
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(cardUrl);
      toast({
        title: "Link Copied!",
        description: "Card link copied to clipboard."
      });
    }
  };

  const handleShareQR = async (card) => {
    try {
      toast({
        title: "Generating QR Code...",
        description: "Please wait..."
      });

      // Navigate to card page to trigger QR share
      navigate(`/bio/${card.username}`, { state: { openQR: true } });
    } catch (error) {
      console.error('Error sharing QR code:', error);
      toast({
        title: "Error",
        description: "Failed to share QR code.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCard = async (card) => {
    // Use username in confirmation to differentiate cards with same name
    const confirmMessage = `Are you sure you want to delete "${card.name}" (@${card.username})? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Get unique identifiers before async operations
    const cardId = card.id || card._id;
    const username = card.username;
    const cardName = card.name;

    try {
      // Show loading toast
      toast({
        title: "Deleting card...",
        description: "Please wait..."
      });

      const { cardAPI } = await import('../services/api');
      
      console.log('🗑️ Deleting card:', {
        username: username,
        name: cardName,
        id: cardId
      });
      
      // Delete the card from backend using username - wait for completion
      const deleteResponse = await cardAPI.delete(username);
      
      console.log('✅ Card deleted from backend, response:', deleteResponse);
      
      // Wait a moment to ensure backend has processed the delete
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Immediately remove from UI state using multiple identifiers
      setUserCards(prevCards => {
        const filtered = prevCards.filter(c => {
          // Use all possible identifiers to ensure correct card is removed
          const isMatch = c.username === username || 
                         (c.id && c.id === cardId) || 
                         (c._id && c._id === cardId);
          
          if (isMatch) {
            console.log(`🗑️ Removing card from UI:`, {
              username: c.username,
              name: c.name,
              id: c.id || c._id
            });
          }
          
          return !isMatch;
        });
        console.log(`📊 Cards count - Before: ${prevCards.length}, After: ${filtered.length}`);
        return filtered;
      });
      
      // Remove from analytics state too
      setCardAnalytics(prev => {
        const newAnalytics = { ...prev };
        delete newAnalytics[cardId];
        console.log('📊 Analytics cleaned for cardId:', cardId);
        return newAnalytics;
      });
      
      // Wait another moment before showing success
      await new Promise(resolve => setTimeout(resolve, 100));
      
      toast({
        title: "Card Deleted",
        description: `"${cardName}" (@${username}) has been deleted successfully.`
      });
      
      console.log('✅ Delete operation completed successfully');
      
    } catch (error) {
      console.error('❌ Error deleting card:', error);
      console.error('Error details:', error.response?.data);
      
      toast({
        title: "Error deleting card",
        description: error.response?.data?.detail || error.message || "Failed to delete card. Please try again.",
        variant: "destructive"
      });
      
      // If delete failed, trigger full refresh to show correct state
      console.log('⚠️ Delete failed, triggering full refresh');
      setRefreshTrigger(prev => prev + 1);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading || !user) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout userCard={userCard} loading={loading}>
      <SEO 
        title="My Profile - Piper Card"
        description="Manage your digital business cards and view your scanned contacts."
      />
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
          {/* Profile Header - CRM Style */}
          <div className="bg-white shadow-sm rounded-lg mb-4">
            <div className="px-3 sm:px-4 py-2 sm:py-3">
              <div className="flex items-center gap-3 sm:gap-4">
                {/* Avatar with Upload Button */}
                <div className="relative group">
                  <Avatar className="w-10 h-10 sm:w-12 sm:h-12 border-2 border-gray-200">
                    <AvatarImage src={user.photoURL} alt={user.displayName} />
                    <AvatarFallback className="bg-amber-600 text-white text-lg sm:text-xl font-semibold">
                      {user.displayName?.charAt(0) || user.email?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 text-white" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">{user.displayName || 'User'}</h1>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-gray-600 text-xs">
                    <span className="flex items-center gap-1 truncate">
                      <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{user.email}</span>
                    </span>
                    <button 
                      onClick={() => {
                        setPhoneNumber(user.phoneNumber || '');
                        setEditPhoneOpen(true);
                      }}
                      className="flex items-center gap-1 hover:text-amber-600 transition-colors"
                      data-testid="edit-phone-btn"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      {user.phoneNumber ? user.phoneNumber : <span className="text-amber-600">Add Phone</span>}
                    </button>
                    {subscriptionStatus && premiumEnabled && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        subscriptionStatus?.tier === 'business' ? 'bg-amber-500 text-white' :
                        subscriptionStatus?.tier === 'pro' ? 'bg-amber-600 text-white' :
                        'bg-gray-200 text-gray-700'
                      }`}>
                        {subscriptionStatus?.tier === 'business' ? 'Business' :
                         subscriptionStatus?.tier === 'pro' ? 'Pro' : 'Free'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Link to="/create">
                    <Button className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs">
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                      <span className="hidden sm:inline">Create Card</span>
                      <span className="sm:hidden">Create</span>
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 h-8"
                    onClick={handleSignOut}
                  >
                    <LogOut className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Account Subscription Section - For Scan & CRM Features */}
          {premiumEnabled && (
            <div className="mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-500" />
                      Account Subscription
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">For Scan & CRM Features</p>
                  </div>
                  {subscriptionStatus?.hasAccess ? (
                    <span className="px-3 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-semibold rounded-full">
                      {subscriptionStatus?.tier === 'business' ? 'Business' : 'Pro'}
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs font-semibold rounded-full">
                      Free
                    </span>
                  )}
                </div>
                
                {subscriptionStatus?.hasAccess ? (
                  <div className="space-y-3">
                    {/* Features Included */}
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200 mb-3">
                      <p className="text-xs font-medium text-green-800 mb-2">Features Included:</p>
                      <ul className="text-xs text-green-700 space-y-1">
                        <li className="flex items-center gap-1">✓ Unlimited Card Scans</li>
                        <li className="flex items-center gap-1">✓ CRM & Lead Management</li>
                        <li className="flex items-center gap-1">✓ Advanced Analytics</li>
                        {subscriptionStatus?.tier === 'business' && (
                          <li className="flex items-center gap-1">✓ Team Collaboration</li>
                        )}
                      </ul>
                    </div>
                    
                    {/* Subscription Details */}
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Plan Type</span>
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {subscriptionStatus?.subscriptionType === 'monthly' ? 'Monthly' :
                         subscriptionStatus?.subscriptionType === 'yearly' ? 'Yearly' :
                         subscriptionStatus?.subscriptionType || 'Active'}
                      </span>
                    </div>
                    
                    {/* End Date */}
                    {subscriptionStatus?.endDate && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-600">
                          {subscriptionStatus?.autoRenew ? 'Renews On' : 'Expires On'}
                        </span>
                        <span className={`text-sm font-medium ${
                          new Date(subscriptionStatus.endDate) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                            ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          {new Date(subscriptionStatus.endDate).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                    
                    {/* Auto Renew Status */}
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-gray-600">Auto Renewal</span>
                      <span className={`text-sm font-medium ${subscriptionStatus?.autoRenew ? 'text-green-600' : 'text-gray-500'}`}>
                        {subscriptionStatus?.autoRenew ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="py-4">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                      <p className="text-xs font-medium text-gray-700 mb-2">Unlock with Pro or Business:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li className="flex items-center gap-1">• Unlimited Card Scans</li>
                        <li className="flex items-center gap-1">• CRM & Lead Management</li>
                        <li className="flex items-center gap-1">• Advanced Analytics</li>
                        <li className="flex items-center gap-1">• Team Collaboration (Business)</li>
                      </ul>
                    </div>
                    <div className="flex gap-2">
                      <Link to="/pricing" className="flex-1">
                        <Button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-sm">
                          <Crown className="w-4 h-4 mr-1" />
                          Get Pro
                        </Button>
                      </Link>
                      <Link to="/pricing" className="flex-1">
                        <Button variant="outline" className="w-full border-amber-500 text-amber-600 hover:bg-amber-50 text-sm">
                          <Sparkles className="w-4 h-4 mr-1" />
                          Get Business
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* My Cards Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-amber-600" />
                  My Cards
                  <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs font-medium">
                    {userCards.length}
                  </span>
                </h2>
                <p className="text-xs text-gray-500 mt-1">Card Premium = Premium features for individual cards</p>
              </div>
              {cardsNeedingPremium.length > 0 && (
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-xs"
                  onClick={() => setShowCardPremiumModal(true)}
                >
                  <Crown className="w-3 h-3 mr-1" />
                  Buy Card Premium ({cardsNeedingPremium.length})
                </Button>
              )}
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              {loadingCards ? (
                <div className="text-center py-12">
                  <div className="w-10 h-10 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-gray-500 text-sm">Loading your cards...</p>
                </div>
              ) : userCards.length === 0 ? (
                <div className="text-center py-12">
                  <QrCode className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">You haven&apos;t created any cards yet</p>
                  <Link to="/create">
                    <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create Your First Card
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {userCards.map((card) => {
                    const cardId = card.id || (typeof card._id === 'object' ? String(card._id) : card._id);
                    const stats = cardAnalytics[cardId] || { views: 0, saves: 0 };
                    const viewCount = typeof stats === 'object' ? stats.views : stats;
                    const saveCount = typeof stats === 'object' ? stats.saves : 0;
                    
                    // Check card premium status
                    const isNfcCard = !!card.nfc_code;
                    const isPremiumCard = card.is_premium_card === true;
                    const premiumExpiresAt = card.premium_expires_at ? new Date(card.premium_expires_at) : null;
                    const isPremiumActive = isPremiumCard && premiumExpiresAt && premiumExpiresAt > new Date();
                    const daysRemaining = premiumExpiresAt ? Math.ceil((premiumExpiresAt - new Date()) / (1000 * 60 * 60 * 24)) : 0;
                    
                    return (
                      <div 
                        key={cardId}
                        className="relative group"
                      >
                        {/* Stats Badges - Top Right Corner */}
                        <div className="absolute top-4 right-4 z-10 flex gap-2">
                          <div className="bg-white rounded-full px-2.5 py-1 shadow-md border border-gray-200 flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5 text-gray-600" />
                            <span className="text-xs font-medium text-gray-700">{viewCount}</span>
                          </div>
                          <div className="bg-white rounded-full px-2.5 py-1 shadow-md border border-green-200 flex items-center gap-1">
                            <Download className="w-3.5 h-3.5 text-green-600" />
                            <span className="text-xs font-medium text-green-700">{saveCount}</span>
                          </div>
                        </div>

                        {/* Clickable Card - Navigates to Bio Page */}
                        <div 
                          onClick={() => navigate(`/bio/${card.username}`)}
                          className="block cursor-pointer hover:shadow-lg transition-shadow duration-200 rounded-lg overflow-hidden"
                        >
                          <VisitingCardDisplay card={card} compact={true} />
                        </div>
                        
                        {/* Card Premium Status Badge - Below card */}
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {isNfcCard ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                                  <Sparkles className="w-3 h-3" />
                                  NFC Card
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                  <QrCode className="w-3 h-3" />
                                  Digital Card
                                </span>
                              )}
                              
                              {isPremiumActive ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                  <Crown className="w-3 h-3" />
                                  Premium Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                  <Crown className="w-3 h-3" />
                                  Free
                                </span>
                              )}
                            </div>
                            
                            {/* Analytics Button - Only for premium cards */}
                            {isPremiumActive && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleViewAnalytics(card);
                                }}
                                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-3 py-1.5 shadow-sm hover:shadow-md transition-all flex items-center gap-1.5"
                                title="View Analytics"
                                data-testid={`analytics-btn-${card.username}`}
                              >
                                <BarChart3 className="w-3.5 h-3.5" />
                                <span className="text-xs font-medium">Analytics</span>
                              </button>
                            )}
                          </div>
                          
                          {/* Premium Details */}
                          {isPremiumActive && premiumExpiresAt && (
                            <div className="mt-2 text-xs text-gray-600">
                              <span className="font-medium">Premium until:</span>{' '}
                              {premiumExpiresAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              {daysRemaining <= 30 && (
                                <span className="ml-2 text-amber-600 font-medium">
                                  ({daysRemaining} days left)
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Subscribe Button for non-premium cards */}
                          {!isPremiumActive && (
                            <div className="mt-2">
                              <Button 
                                size="sm" 
                                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-xs"
                                data-testid={`subscribe-btn-${card.username}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedCardForPremium(card);
                                  setShowCardPremiumModal(true);
                                }}
                              >
                                <Crown className="w-3 h-3 mr-1" />
                                {isNfcCard ? 'Renew Premium' : 'Get Card Premium'}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Orders Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-600" />
                My Orders
                {orders.length > 0 && (
                  <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs font-medium">
                    {orders.length}
                  </span>
                )}
              </h2>
              {orders.length > 0 && (
                <Link to="/orders" className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {loadingOrders ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-gray-500 text-sm">Loading orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm mb-3">No orders yet</p>
                  <Link to="/shop">
                    <Button variant="outline" size="sm" className="text-amber-600 border-amber-300 hover:bg-amber-50">
                      Browse Shop
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {orders.map((order) => (
                    <Link
                      key={order._id || order.id}
                      to="/orders"
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          order.status === 'delivered' ? 'bg-green-500' :
                          order.status === 'shipped' ? 'bg-blue-500' :
                          order.status === 'processing' ? 'bg-amber-500' :
                          order.status === 'cancelled' ? 'bg-red-500' :
                          'bg-gray-400'
                        }`} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Order #{(order._id || order.id)?.slice(-8).toUpperCase()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''} • ₹{order.totalAmount?.toLocaleString() || 0}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                          order.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                          order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {order.status?.charAt(0).toUpperCase() + order.status?.slice(1) || 'Pending'}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Modal */}
      <Dialog open={viewDetailsOpen} onOpenChange={setViewDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-amber-600" />
              Card Analytics - {selectedCardViews?.card?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingViewDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : selectedCardViews ? (
            <div className="space-y-6">
              {/* Stats Summary */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="border border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Total Views</p>
                    <p className="text-3xl font-bold text-gray-900">{selectedCardViews.totalViews}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600">Unique Viewers</p>
                    <p className="text-3xl font-bold text-teal-600">{selectedCardViews.uniqueViewers}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Views List */}
              {selectedCardViews.views && selectedCardViews.views.length > 0 ? (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Recent Views</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {selectedCardViews.views.map((view, index) => (
                      <div key={index} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium">{view.location || 'Unknown Location'}</p>
                            <p className="text-xs text-gray-500">
                              {view.device || 'Unknown Device'} • {view.browser || 'Unknown Browser'}
                            </p>
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(view.timestamp).toLocaleDateString()} {new Date(view.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No views yet. Share your card to start tracking!</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      
      {/* Edit Phone Dialog */}
      <Dialog open={editPhoneOpen} onOpenChange={setEditPhoneOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              {user?.phoneNumber ? 'Update Phone Number' : 'Add Phone Number'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Mobile Number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter 10-digit mobile number"
                className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                data-testid="phone-edit-input"
              />
              <p className="text-xs text-gray-500 mt-1">Used for order updates & delivery</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setEditPhoneOpen(false)}
                disabled={savingPhone}
              >
                Cancel
              </Button>
              <Button 
                className="bg-amber-500 hover:bg-amber-600"
                onClick={handleSavePhone}
                disabled={savingPhone || phoneNumber.length !== 10}
                data-testid="save-phone-btn"
              >
                {savingPhone ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Analytics Modal */}
      <AnalyticsModal
        isOpen={showAnalyticsModal}
        onClose={() => {
          setShowAnalyticsModal(false);
          setSelectedCardForAnalytics(null);
        }}
        cardId={selectedCardForAnalytics?.id}
        cardName={selectedCardForAnalytics?.name}
      />
      
      {/* Card Premium Purchase Modal */}
      <Dialog open={showCardPremiumModal} onOpenChange={(open) => {
        setShowCardPremiumModal(open);
        if (!open) setSelectedCardForPremium(null);
      }}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Crown className="w-5 h-5" />
              Buy Card Premium
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-4">
              {/* Card Selection */}
              <div>
                <p className="text-sm text-gray-600 mb-2">Select which card to upgrade:</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {cardsNeedingPremium.map((card) => (
                    <div 
                      key={card.id || card.username}
                      onClick={() => setSelectedCardForPremium(card)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedCardForPremium?.username === card.username 
                          ? 'bg-amber-50 border-amber-400 ring-2 ring-amber-200' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          card.nfc_code ? 'bg-amber-100' : 'bg-gray-100'
                        }`}>
                          {card.nfc_code ? (
                            <Sparkles className="w-4 h-4 text-amber-600" />
                          ) : (
                            <QrCode className="w-4 h-4 text-gray-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">@{card.username}</p>
                          <p className="text-xs text-gray-500">{card.name}</p>
                        </div>
                        {selectedCardForPremium?.username === card.username && (
                          <Check className="w-5 h-5 text-amber-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Premium Benefits */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-800 mb-2">Card Premium includes:</p>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li className="flex items-center gap-1"><Check className="w-3 h-3" /> Premium card templates</li>
                  <li className="flex items-center gap-1"><Check className="w-3 h-3" /> Custom logo on card</li>
                  <li className="flex items-center gap-1"><Check className="w-3 h-3" /> No Piper Card branding</li>
                  <li className="flex items-center gap-1"><Check className="w-3 h-3" /> Priority support</li>
                </ul>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowCardPremiumModal(false);
                    setSelectedCardForPremium(null);
                  }}
                >
                  Cancel
                </Button>
                <Link 
                  to={selectedCardForPremium ? `/card-premium?card=${selectedCardForPremium.username}` : '#'}
                  className="flex-1"
                  onClick={(e) => {
                    if (!selectedCardForPremium) {
                      e.preventDefault();
                      toast({
                        title: "Select a card",
                        description: "Please select which card you want to upgrade",
                        variant: "destructive"
                      });
                    }
                  }}
                >
                  <Button 
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                    disabled={!selectedCardForPremium}
                  >
                    <Crown className="w-4 h-4 mr-1" />
                    Buy Premium
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Profile;
